import { HttpApiService } from "@/shared/services/httpApi.service";

import type {
  Experiment,
  ExperimentOverride,
  ExperimentResults,
  ExperimentUser,
  ExperimentVariant,
  ExperimentStatus,
  OtcDay,
  OtcDayValues,
  OtcMetrics,
  OtcMetricsChange,
  OtcMetricsPeriod,
  OtcPeriodMetrics,
  OtcSideMetrics,
  TpvCorridor,
  TpvCorridorValues,
  TpvCustody,
  TpvCustodyCurrency,
  TpvDay,
  TpvFlowMetrics,
  TpvFxPair,
  TpvInFlightValues,
  TpvMetrics,
  TpvMetricsChange,
  TpvPeriodMetrics,
  TpvOrganization,
} from "../types/metrics.types";

const EMPTY_PERIOD: OtcPeriodMetrics = {
  transactions: 0,
  volume: "0",
  asset_volume: "0",
  average_ticket: null,
  average_asset_ticket: null,
  average_price: null,
  users: 0,
  new_users: 0,
  organizations: 0,
  transactions_per_user: null,
  duration: null,
  not_completed: { count: 0, rate: null, by_reason: {} },
  conversion: null,
};

const EMPTY_CHANGE: OtcMetricsChange = {
  transactions: null,
  volume: null,
  asset_volume: null,
  average_ticket: null,
  average_asset_ticket: null,
  average_price: null,
  users: null,
  new_users: null,
  transactions_per_user: null,
  duration_p50: null,
  not_completed_rate: null,
  conversion_rate: null,
};

function readPeriod(source: unknown): OtcPeriodMetrics {
  const period = (source ?? {}) as Partial<OtcPeriodMetrics>;
  return {
    ...EMPTY_PERIOD,
    ...period,
    not_completed: { ...EMPTY_PERIOD.not_completed, ...period.not_completed },
    duration: period.duration ?? null,
    conversion: period.conversion ?? null,
  };
}

function readSide(source: unknown): OtcSideMetrics {
  const side = (source ?? {}) as Partial<OtcSideMetrics>;
  return {
    current: readPeriod(side.current),
    previous: readPeriod(side.previous),
    change: { ...EMPTY_CHANGE, ...side.change },
    top_users: Array.isArray(side.top_users) ? side.top_users : [],
    quoters: Array.isArray(side.quoters) ? side.quoters : [],
  };
}

function readDayValues(source: Partial<OtcDayValues> | undefined): OtcDayValues {
  return {
    transactions: source?.transactions ?? 0,
    volume: source?.volume ?? "0",
    asset_volume: source?.asset_volume ?? "0",
    average_price: source?.average_price ?? null,
  };
}

function readDays(source: unknown): OtcDay[] {
  if (!Array.isArray(source)) return [];
  return source
    .filter((day): day is OtcDay => typeof (day as OtcDay | null)?.date === "string")
    .map((day) => ({
      date: day.date,
      buy: readDayValues(day.buy),
      sell: readDayValues(day.sell),
      card_top_up: readDayValues(day.card_top_up),
    }));
}

export function normalizeOtcMetrics(payload: unknown): OtcMetrics {
  const source = (payload ?? {}) as Partial<OtcMetrics>;
  return {
    days: typeof source.days === "number" ? source.days : 7,
    current: source.current ?? { from: "", to: "" },
    previous: source.previous ?? { from: "", to: "" },
    currency: source.currency ?? "COP",
    asset: source.asset ?? "USDT",
    buy: readSide(source.buy),
    sell: readSide(source.sell),
    card_top_up: readSide(source.card_top_up),
    total: readSide(source.total),
    daily: {
      current: readDays(source.daily?.current),
      previous: readDays(source.daily?.previous),
    },
  };
}

const EMPTY_TPV_PERIOD: TpvPeriodMetrics = {
  transactions: 0,
  volume_usd: "0",
  average_ticket_usd: null,
  revenue: { total_usd: "0", service_usd: "0", conversion_usd: "0", fx_execution_usd: "0" },
  take_rate: null,
  organizations: 0,
  new_organizations: 0,
  not_completed: { count: 0, rate: null, by_reason: {} },
  expired: 0,
  unconverted_transactions: 0,
};

const EMPTY_TPV_CHANGE: TpvMetricsChange = {
  transactions: null,
  volume_usd: null,
  average_ticket_usd: null,
  revenue_usd: null,
  take_rate: null,
  organizations: null,
  new_organizations: null,
  not_completed_rate: null,
};

const EMPTY_CORRIDOR_VALUES: TpvCorridorValues = {
  transactions: 0,
  volume: "0",
  volume_usd: "0",
  revenue_usd: "0",
};

function arrayOf<T>(source: unknown, read: (row: Record<string, unknown>) => T | null): T[] {
  if (!Array.isArray(source)) return [];
  return source.flatMap((row) => {
    const value = read((row ?? {}) as Record<string, unknown>);
    return value === null ? [] : [value];
  });
}

function readTpvPeriod(source: unknown): TpvPeriodMetrics {
  const period = (source ?? {}) as Partial<TpvPeriodMetrics>;
  return {
    ...EMPTY_TPV_PERIOD,
    ...period,
    revenue: { ...EMPTY_TPV_PERIOD.revenue, ...period.revenue },
    not_completed: { ...EMPTY_TPV_PERIOD.not_completed, ...period.not_completed },
  };
}

function readTpvFlow(source: unknown): TpvFlowMetrics {
  const flow = (source ?? {}) as Partial<TpvFlowMetrics>;
  return {
    current: readTpvPeriod(flow.current),
    previous: readTpvPeriod(flow.previous),
    change: { ...EMPTY_TPV_CHANGE, ...flow.change },
  };
}

function readInFlight(source: unknown): TpvInFlightValues {
  const values = (source ?? {}) as Partial<TpvInFlightValues>;
  return { transactions: values.transactions ?? 0, volume_usd: values.volume_usd ?? "0" };
}

function readCorridorValues(source: unknown): TpvCorridorValues {
  return { ...EMPTY_CORRIDOR_VALUES, ...((source ?? {}) as Partial<TpvCorridorValues>) };
}

function readCorridor(row: Record<string, unknown>): TpvCorridor | null {
  const product = textOrNull(row.product) as TpvCorridor["product"] | null;
  if (!product) return null;
  return {
    direction: row.direction === "in" ? "in" : "out",
    product,
    rail: textOrNull(row.rail) ?? "",
    country: textOrNull(row.country) ?? "",
    currency: textOrNull(row.currency) ?? "",
    source_currency: textOrNull(row.source_currency) ?? textOrNull(row.currency) ?? "",
    current: readCorridorValues(row.current),
    previous: readCorridorValues(row.previous),
    share: typeof row.share === "number" ? row.share : null,
    change: typeof row.change === "number" ? row.change : null,
  };
}

function readFxPair(row: Record<string, unknown>): TpvFxPair | null {
  const origin = textOrNull(row.origin_currency);
  const destination = textOrNull(row.destination_currency);
  if (!origin || !destination) return null;
  return {
    origin_currency: origin,
    destination_currency: destination,
    current: readCorridorValues(row.current),
    previous: readCorridorValues(row.previous),
    change: typeof row.change === "number" ? row.change : null,
  };
}

function readOrganization(row: Record<string, unknown>): TpvOrganization | null {
  const id = textOrNull(row.organization_id);
  if (!id) return null;
  const number = (value: unknown) => (typeof value === "number" ? value : 0);
  return {
    organization_id: id,
    name: textOrNull(row.name),
    payins_usd: textOrNull(row.payins_usd) ?? "0",
    payouts_usd: textOrNull(row.payouts_usd) ?? "0",
    tpv_usd: textOrNull(row.tpv_usd) ?? "0",
    fx_usd: textOrNull(row.fx_usd) ?? "0",
    transactions_in: number(row.transactions_in),
    transactions_out: number(row.transactions_out),
    transactions: number(row.transactions),
    revenue_usd: textOrNull(row.revenue_usd) ?? "0",
    share: typeof row.share === "number" ? row.share : null,
    previous_tpv_usd: textOrNull(row.previous_tpv_usd) ?? "0",
    change: typeof row.change === "number" ? row.change : null,
  };
}

function readOrganizationRef(source: unknown): TpvMetrics["organization"] {
  const row = (source ?? {}) as Record<string, unknown>;
  const id = textOrNull(row.id);
  return id ? { id, name: textOrNull(row.name) } : null;
}

function readCustodyCurrency(row: Record<string, unknown>): TpvCustodyCurrency | null {
  const currency = textOrNull(row.currency);
  if (!currency) return null;
  const net = (row.net_change ?? {}) as Partial<TpvCustodyCurrency["net_change"]>;
  return {
    currency,
    wallets: typeof row.wallets === "number" ? row.wallets : 0,
    available: textOrNull(row.available) ?? "0",
    pending: textOrNull(row.pending) ?? "0",
    blocked: textOrNull(row.blocked) ?? "0",
    available_usd: textOrNull(row.available_usd),
    pending_usd: textOrNull(row.pending_usd),
    blocked_usd: textOrNull(row.blocked_usd),
    x_per_usd: textOrNull(row.x_per_usd),
    net_change: { current: net.current ?? "0", previous: net.previous ?? "0" },
  };
}

function readCustody(source: unknown): TpvCustody {
  const custody = (source ?? {}) as Partial<TpvCustody>;
  return {
    as_of: custody.as_of ?? "",
    wallets: custody.wallets ?? 0,
    new_wallets: { current: 0, previous: 0, ...custody.new_wallets },
    active_wallets: { current: 0, previous: 0, ...custody.active_wallets },
    available_usd: custody.available_usd ?? "0",
    pending_usd: custody.pending_usd ?? "0",
    blocked_usd: custody.blocked_usd ?? "0",
    by_currency: arrayOf(custody.by_currency, readCustodyCurrency),
  };
}

function readTpvDay(row: Record<string, unknown>): TpvDay | null {
  const date = textOrNull(row.date);
  if (!date) return null;
  return {
    date,
    payins_usd: textOrNull(row.payins_usd) ?? "0",
    payouts_usd: textOrNull(row.payouts_usd) ?? "0",
    fx_usd: textOrNull(row.fx_usd) ?? "0",
    transactions_in: typeof row.transactions_in === "number" ? row.transactions_in : 0,
    transactions_out: typeof row.transactions_out === "number" ? row.transactions_out : 0,
    revenue_usd: textOrNull(row.revenue_usd) ?? "0",
  };
}

export function normalizeTpvMetrics(payload: unknown): TpvMetrics {
  const source = (payload ?? {}) as Partial<TpvMetrics>;
  const inFlight = (source.in_flight ?? {}) as Partial<TpvMetrics["in_flight"]>;
  const rates = (source.rates ?? {}) as Partial<TpvMetrics["rates"]>;
  return {
    days: typeof source.days === "number" ? source.days : 30,
    current: source.current ?? { from: "", to: "" },
    previous: source.previous ?? { from: "", to: "" },
    currency: source.currency ?? "USD",
    organization: readOrganizationRef(source.organization),
    total: readTpvFlow(source.total),
    payins: readTpvFlow(source.payins),
    payouts: readTpvFlow(source.payouts),
    fx: readTpvFlow(source.fx),
    top_ups: readTpvFlow(source.top_ups),
    in_flight: { payins: readInFlight(inFlight.payins), payouts: readInFlight(inFlight.payouts) },
    corridors: arrayOf(source.corridors, readCorridor),
    fx_pairs: arrayOf(source.fx_pairs, readFxPair),
    organizations: arrayOf(source.organizations, readOrganization),
    custody: readCustody(source.custody),
    daily: {
      current: arrayOf(source.daily?.current, readTpvDay),
      previous: arrayOf(source.daily?.previous, readTpvDay),
    },
    rates: {
      filled_share: typeof rates.filled_share === "number" ? rates.filled_share : null,
      missing_currencies: Array.isArray(rates.missing_currencies)
        ? rates.missing_currencies.filter((code): code is string => typeof code === "string")
        : [],
      latest_rate_at: typeof rates.latest_rate_at === "string" ? rates.latest_rate_at : null,
    },
  };
}

function readExperiment(source: unknown): Experiment | null {
  const row = (source ?? {}) as Partial<Experiment>;
  if (typeof row.key !== "string" || typeof row.status !== "string") return null;
  return {
    key: row.key,
    description: row.description ?? "",
    status: row.status,
    variants: Array.isArray(row.variants) ? row.variants : [],
    control_variant: row.control_variant ?? "control",
    rollout_variant: row.rollout_variant ?? "treatment",
    started_at: row.started_at ?? "",
    ended_at: row.ended_at ?? null,
    users_by_variant: Array.isArray(row.users_by_variant) ? row.users_by_variant : [],
  };
}

export function normalizeExperiments(payload: unknown): Experiment[] {
  const rows = (payload as { experiments?: unknown } | null)?.experiments;
  if (!Array.isArray(rows)) return [];
  return rows.map(readExperiment).filter((row): row is Experiment => row !== null);
}

function textOrNull(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

function readOverride(source: unknown): ExperimentOverride | null {
  const row = (source ?? {}) as Record<string, unknown>;
  const userId = textOrNull(row.user_id);
  const variant = textOrNull(row.variant);
  if (!userId || !variant) return null;
  const forcedAt = textOrNull(row.forced_at);
  return {
    user_id: userId,
    email: textOrNull(row.email),
    name: textOrNull(row.name),
    organization_name: textOrNull(row.organization_name),
    variant,
    forced_at: forcedAt && !Number.isNaN(Date.parse(forcedAt)) ? forcedAt : null,
  };
}

export function normalizeExperimentResults(payload: unknown): ExperimentResults | null {
  const source = (payload ?? {}) as Partial<ExperimentResults>;
  const experiment = readExperiment(source.experiment);
  if (!experiment) return null;
  return {
    experiment,
    overrides: Array.isArray(source.overrides)
      ? source.overrides.map(readOverride).filter((row): row is ExperimentOverride => row !== null)
      : [],
    metric: typeof source.metric === "string" ? source.metric : null,
    value_currency: typeof source.value_currency === "string" ? source.value_currency : null,
    groups: Array.isArray(source.groups) ? source.groups : [],
    comparisons: Array.isArray(source.comparisons) ? source.comparisons : [],
    timeline: Array.isArray(source.timeline) ? source.timeline : [],
  };
}

export function normalizeExperimentUsers(payload: unknown): ExperimentUser[] {
  const rows = (payload as { users?: unknown } | null)?.users;
  if (!Array.isArray(rows)) return [];
  return rows.flatMap((source) => {
    const row = (source ?? {}) as Record<string, unknown>;
    const userId = textOrNull(row.user_id);
    const email = textOrNull(row.email);
    const variant = textOrNull(row.variant);
    if (!userId || !email || !variant) return [];
    return [
      {
        user_id: userId,
        email,
        name: textOrNull(row.name),
        organization_name: textOrNull(row.organization_name),
        variant,
        source: textOrNull(row.source) ?? "not_assigned",
        natural_variant: textOrNull(row.natural_variant) ?? variant,
        assigned_at: textOrNull(row.assigned_at),
      },
    ];
  });
}

export const MetricsService = {
  async access(): Promise<boolean> {
    const { data } = await HttpApiService.get<{ allowed: boolean }>("/internal/metrics/access");
    return data.allowed === true;
  },

  async otc(days: OtcMetricsPeriod): Promise<OtcMetrics> {
    const { data } = await HttpApiService.get<unknown>(`/internal/metrics/otc?days=${days}`);
    return normalizeOtcMetrics(data);
  },

  async tpv(days: OtcMetricsPeriod, organizationId: string | null = null): Promise<TpvMetrics> {
    const query = new URLSearchParams({ days: String(days) });
    if (organizationId) query.set("organization_id", organizationId);
    const { data } = await HttpApiService.get<unknown>(`/internal/metrics/tpv?${query}`);
    return normalizeTpvMetrics(data);
  },

  async experiments(): Promise<Experiment[]> {
    const { data } = await HttpApiService.get<unknown>("/internal/experiments");
    return normalizeExperiments(data);
  },

  async experimentResults(key: string): Promise<ExperimentResults | null> {
    const { data } = await HttpApiService.get<unknown>(
      `/internal/experiments/${encodeURIComponent(key)}/results`,
    );
    return normalizeExperimentResults(data);
  },

  async lookupUser(key: string, email: string): Promise<ExperimentUser> {
    const { data } = await HttpApiService.get<ExperimentUser>(
      `/internal/experiments/${encodeURIComponent(key)}/users?email=${encodeURIComponent(email)}`,
    );
    return data;
  },

  async searchUsers(key: string, query: string): Promise<ExperimentUser[]> {
    const { data } = await HttpApiService.get<unknown>(
      `/internal/experiments/${encodeURIComponent(key)}/users/search?q=${encodeURIComponent(query)}`,
    );
    return normalizeExperimentUsers(data);
  },

  async forceVariant(key: string, email: string, variant: string): Promise<ExperimentOverride> {
    const { data } = await HttpApiService.put<ExperimentOverride>(
      `/internal/experiments/${encodeURIComponent(key)}/overrides`,
      { email, variant },
    );
    return data;
  },

  async releaseVariant(key: string, userId: string): Promise<void> {
    await HttpApiService.delete(
      `/internal/experiments/${encodeURIComponent(key)}/overrides/${encodeURIComponent(userId)}`,
    );
  },

  async updateWeights(key: string, variants: ExperimentVariant[]): Promise<Experiment | null> {
    const { data } = await HttpApiService.put<unknown>(
      `/internal/experiments/${encodeURIComponent(key)}/weights`,
      { variants },
    );
    return readExperiment(data);
  },

  async updateExperiment(key: string, status: ExperimentStatus): Promise<Experiment | null> {
    const { data } = await HttpApiService.patch<unknown>(`/internal/experiments/${encodeURIComponent(key)}`, {
      status,
    });
    return readExperiment(data);
  },
};
